import { create } from 'zustand';
import {
  onAuthStateChanged,
  type User,
} from 'firebase/auth';
import { collection, onSnapshot } from 'firebase/firestore';
import { auth, db } from '../lib/firebase';
import { COLLECTIONS, SUBCOLLECTIONS } from '../lib/collections';
import type { Profile } from '../types/user';

interface AuthState {
  user: User | null;
  accountId: string | null;
  profile: Profile | null;
  loading: boolean;
  initialized: boolean;
  isAnonymous: boolean;

  setUser: (user: User | null) => void;
  setAccountId: (accountId: string | null) => void;
  setProfile: (profile: Profile | null) => void;
  setLoading: (loading: boolean) => void;
  reset: () => void;
  init: () => () => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  accountId: null,
  profile: null,
  loading: true,
  initialized: false,
  isAnonymous: true,

  setUser: (user) => set({ user }),
  setAccountId: (accountId) => set({ accountId }),
  setProfile: (profile) => set({ profile }),
  setLoading: (loading) => set({ loading }),
  reset: () => set({ user: null, accountId: null, profile: null, loading: false, isAnonymous: true }),

  init: () => {
    let profileUnsub: (() => void) | null = null;

    const authUnsub = onAuthStateChanged(auth, (firebaseUser) => {
      if (profileUnsub) {
        profileUnsub();
        profileUnsub = null;
      }

      if (firebaseUser) {
        const isAnon = firebaseUser.isAnonymous;

        set({
          user: firebaseUser,
          isAnonymous: isAnon,
          // Anonymous users have no profile to load — clear loading immediately.
          // Registered users wait for the profile snapshot before loading clears.
          loading: !isAnon,
          initialized: true,
        });

        if (!isAnon) {
          // Fetch the stable accountId from custom claims first, then start the
          // profile listener using that UUID (not the Firebase UID, which differs
          // under the new multi-platform backend schema).
          void (async () => {
            const tokenResult = await firebaseUser.getIdTokenResult();
            const claimedAccountId = (tokenResult.claims.accountId as string) ?? null;
            set({ accountId: claimedAccountId });

            if (!claimedAccountId) {
              set({ loading: false });
              return;
            }

            // Real-time listener on the profiles subcollection so changes
            // (consent approved, subscription updated) propagate instantly
            // without requiring a page refresh.
            const profilesRef = collection(
              db,
              COLLECTIONS.USERS,
              claimedAccountId,
              SUBCOLLECTIONS.PROFILES,
            );
            profileUnsub = onSnapshot(
              profilesRef,
              (snap) => {
                const profiles = snap.docs.map((d) => ({ id: d.id, ...d.data() }) as Profile);
                set({
                  profile: profiles.length === 1 ? profiles[0] : null,
                  loading: false,
                });
              },
              () => {
                set({ profile: null, loading: false });
              },
            );
          })();
        }
      } else {
        // No user at all — clear state and mark initialized.
        // Unlike Posty, omore does not auto-sign-in anonymously on startup;
        // anonymous sessions are created on-demand by AgeGatePage for the
        // under-13 consent flow, or by signInAnonymously() in the auth hooks.
        set({
          user: null,
          accountId: null,
          profile: null,
          loading: false,
          initialized: true,
          isAnonymous: true,
        });
      }
    });

    return () => {
      authUnsub();
      if (profileUnsub) profileUnsub();
    };
  },
}));
